import type { DeviceSignal, SignalDisposition } from '@api/devices/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, CircleCheck, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { isStatus, reviewSignal, unreviewSignal } from '../lib/api';
import { absolute, ago } from '../lib/time';
import { useScope } from '../scope/scope';
import { activityQueryKeys } from '../search/hooks';
import { Button } from '../ui/Button';
import { useToast } from '../ui/toast';

// WRMM-160 (Harry, 2026-10-21): mark an expected or no-action health finding benign or resolved, show a green check,
// and take it out of what needs attention. The finding and its evidence stay; it comes back if it gets worse.

const dispositionLabel: Record<SignalDisposition, string> = { benign: 'Benign', resolved: 'Resolved' };
const by = (review: NonNullable<DeviceSignal['review']>) => (review.byMe ? 'by you' : 'by a teammate');

function useSignalReview(deviceId: string) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  // Today, search, the device's status and its activity all follow reviews.
  const refresh = () => client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId
    && ['telemetry', 'record', 'search', 'facets', ...activityQueryKeys, 'audit'].includes(String(q.queryKey[2])) });
  const failed = (title: string) => (error: unknown) => toast({ tone: 'warning', title,
    body: isStatus(error, 403) ? 'You don’t have permission for that on this device.' : isStatus(error, 409) ? 'The finding has already cleared. Refresh to see it.' : 'Try again.' });
  const review = useMutation({ mutationFn: ({ signalId, disposition, note }: { signalId: string; disposition: SignalDisposition; note: string }) =>
    reviewSignal(mspId, deviceId, signalId, disposition, note), onSuccess: () => void refresh(), onError: failed('The finding couldn’t be marked') });
  const undo = useMutation({ mutationFn: (signalId: string) => unreviewSignal(mspId, deviceId, signalId), onSuccess: () => void refresh(),
    onError: failed('The mark couldn’t be undone') });
  return { review, undo };
}

/**
 * On an open finding: mark it benign or resolved, with an optional note (for those who may). Says so, to everyone, when an
 * earlier mark no longer covers it.
 */
export function ReviewControls({ deviceId, signal, canReview }: { deviceId: string; signal: DeviceSignal; canReview: boolean }) {
  const { review } = useSignalReview(deviceId);
  const [choosing, setChoosing] = useState<SignalDisposition | null>(null);
  const [note, setNote] = useState('');
  const lapsed = signal.review && !signal.review.covers ? signal.review : null;
  if (!canReview && !lapsed) return null;
  return (
    <div className="mt-3 border-t border-line pt-3">
      {lapsed && (
        <p className={canReview ? 'mb-2 text-[12.5px] text-ink-2' : 'text-[12.5px] text-ink-2'} title={absolute(lapsed.at)}>
          Marked {dispositionLabel[lapsed.disposition].toLowerCase()} {by(lapsed)} {ago(lapsed.at)}, but it has changed since, so it needs a look again.
        </p>
      )}
      {!canReview ? null : choosing ? (
        <form className="flex flex-wrap items-center gap-2" onSubmit={e => {
          e.preventDefault();
          review.mutate({ signalId: signal.id, disposition: choosing, note: note.trim() }, { onSuccess: () => { setChoosing(null); setNote(''); } });
        }}>
          <input value={note} onChange={e => setNote(e.target.value)} maxLength={500} autoFocus aria-label="Why"
            placeholder={choosing === 'benign' ? 'Why is it expected? (optional)' : 'What fixed it? (optional)'}
            className="h-9 min-w-0 flex-1 rounded-lg border border-line bg-panel px-3 text-[13px] outline-none focus:border-brand/50" />
          <Button size="sm" variant="ghost" onClick={() => setChoosing(null)}>Cancel</Button>
          <Button size="sm" variant="primary" type="submit" disabled={review.isPending}><Check className="size-3.5" />Mark {choosing}</Button>
          <p className="w-full text-[12px] text-ink-3">It moves to Reviewed and stops counting as needing attention; the finding and its evidence stay. It comes
            back if it gets worse{signal.key === 'disk.low' || signal.key === 'disk.filling' ? ' or another volume is affected' : ''}, or clears and returns. You can undo it.</p>
        </form>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setChoosing('benign')}><Check className="size-3.5" />Mark benign</Button>
          <Button size="sm" variant="secondary" onClick={() => setChoosing('resolved')}><CircleCheck className="size-3.5" />Mark resolved</Button>
        </div>
      )}
    </div>
  );
}

/** Open findings someone marked benign or resolved: a green check, who and why, and an undo. */
export function ReviewedList({ deviceId, signals, canReview }: { deviceId: string; signals: DeviceSignal[]; canReview: boolean }) {
  const { undo } = useSignalReview(deviceId);
  if (!signals.length) return null;
  return (
    <div>
      <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-3">Reviewed</p>
      <ul className="space-y-1">
        {signals.map(s => s.review && (
          <li key={s.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-ink-2">
            <CircleCheck className="size-3.5 shrink-0 text-healthy" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{s.title}</span>
            <span className="shrink-0 text-ink-3" title={absolute(s.review.at)}>
              {dispositionLabel[s.review.disposition]} · {by(s.review)} {ago(s.review.at)}{s.review.note && ` — “${s.review.note}”`}
            </span>
            {canReview && (
              <button type="button" disabled={undo.isPending} onClick={() => undo.mutate(s.id)} aria-label={`Undo the mark on ${s.title}`}
                className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[12.5px] font-medium text-ink-2 hover:bg-hover hover:text-ink disabled:opacity-50">
                <RotateCcw className="size-3" />Undo
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
